import React from "react";
import { Chip, Tooltip } from "@mui/material";
import { thirdPartyStatusConfigData } from "../../../config/AuditStatusConfig";
import { thirdPartyRemarkConfigData } from "../../../config/AuditStatusConfig";
import { Icons } from "../../../config/MuiIcons.config";
import { AppTheme } from "../../../theme/App.theme";

// Audit Status
const { completed, draftAudits, reviewPending } = thirdPartyStatusConfigData;

// Audit Remark
const {
  insufficientDocuments,
  invalidDocuments,
  invalidInfo,
  missingDetails,
  notAvailable,
  success,
} = thirdPartyRemarkConfigData;

// Audit Status Config
export const thirdPartyAuditStatusConfig = [
  {
    label: "All",
    value: "All"
  },
  {
    label: "Draft",
    value: "Draft"
  },
  {
    label: "Review Pending",
    value: "Review Pending"
  },
  {
    label: "Completed",
    value: "Completed"
  },
]

export const thirdPartyDateFrequencyConfig = [
  {
    label: "Week",
    value: "week",
  },
  {
    label: "Month",
    value: "month",
  },
  {
    label: "Quarter",
    value: "quarter",
  },
  {
    label: "Half Year",
    value: "half-year",
  },
  {
    label: "Year",
    value: "year",
  },
  {
    label: "Custom",
    value: "custom",
  },
]

export const thirdPartyRemarkReasonConfig = [
  {
    label: "Invalid Info",
    value: "Invalid Info",
  },
  {
    label: "Missing Details",
    value: "Missing Details",
  },
  {
    label: "Insufficient Documents",
    value: "Insufficient Documents",
  },
  {
    label: "Invalid Documents",
    value: "Invalid Documents",
  },
]


export const dataTableColumnsForNCActivitiesOfThirdPartyAudits = [
  {
    label: "Activity Name",
    isSort: false,
    id: "activityName",
    isIcon: false,
    // columnWidth: "200px"
  },
  {
    label: "Auditor Observation",
    isSort: false,
    id: "auditorObservation",
    isIcon: false,
    columnWidth: "300px"
  },
  {
    label: "CAPA",
    isSort: false,
    id: "capa",
    isIcon: false,
    columnWidth: "300px"
  },
  {
    label: "Attachments",
    isSort: false,
    id: "attachments",
    isIcon: false,
    // columnWidth: "200px"
  },
  {
    label: "Actions",
    isSort: false,
    id: "actions",
    isIcon: false,
    // columnWidth: "200px",
    cellAlign: "center",
    headerAlign: "center",
  },
]


// Function to return chip color based on the audit status
export const getStatusChipDetailsForThirdPartyAudits = (AuditStatus) => {
  if (AuditStatus === draftAudits) {
    return {
      pillColor: "#E0F7FA",
      textColor: "#006064",
      status: draftAudits,
    };
  } else if (AuditStatus === reviewPending) {
    return {
      pillColor: "#FFF3E0",
      textColor: "#E65100",
      status: reviewPending,
    };
  } else if (AuditStatus === completed) {
    return {
      pillColor: "#E8F5E9",
      textColor: "#2E7D32",
      status: completed,
    };
  } else {
    return {
      pillColor: "#E0F7FA",
      textColor: "#006064",
      status: AuditStatus,
    };
  }
};

// Function to return chip color based on the audit remark
export const getRemarkChipDetailsForThirdPartyAudits = (AuditRemark) => {
  const similarRemarks = [insufficientDocuments, invalidDocuments, invalidInfo, missingDetails];

  if (AuditRemark === notAvailable) {
    return {
      pillColor: "#f0f0f0",
      textColor: "#3d3c3c",
      remark: notAvailable,
    };
  } else if (similarRemarks.includes(AuditRemark)) {
    return {
      pillColor: "#ffe6e6",
      textColor: "#d32f2f",
      remark: AuditRemark,
    };
  } else if (AuditRemark === success) {
    return {
      pillColor: "#E8F5E9",
      textColor: "#2E7D32",
      remark: success,
    };
  } else {
    return {
      pillColor: "#adb5bd",
      textColor: "#000000",
      remark: AuditRemark,
    };
  }
};

export const ThirdPartyAuditInfo = () => {
  const ThirdPartyInfo = (
    <div>
      <p>
        <strong>
          To move the audit to{" "}
          <span>Review Pending</span>, please
          ensure the following:
        </strong>
      </p>

      <ul
        style={{
          paddingLeft: "16px",
          margin: 0,
        }}
      >
        <li>
          <strong>Basic Audit Details:</strong> All fields are compulsory
        </li>
        
        <li>
          <strong>Audit Report:</strong> Must upload a valid audit report file
          (PDF, Image, DOC, etc.)
        </li>

        <li>
          <strong>Activity Count:</strong> Total Activities = NC Activities +
          Closed Activities
        </li>

        <li>
          <strong>Auditor Details:</strong> Must include
          <ul
            style={{
              paddingLeft: "16px",
            }}
          >
            <li>Auditor Name</li>
            <li>Contact Number</li>
            <li>Company Name</li>
          </ul>
        </li>

        <li>
          <strong>NC Activities:</strong>
          <ul
            style={{
              paddingLeft: "16px",
            }}
          >
            <li>Total NC activities added should match the entered NC count</li>
            <li>
              Each NC must include:
              <ul
                style={{
                  paddingLeft: "16px",
                }}
              >
                <li>Activity Name</li>
                <li>Auditor Observation</li>
                <li>CAPA</li>
                <li>At least one attachment</li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>

      <p style={{ marginTop: "8px" }}>
        <strong>Note:</strong> If any of the above are missing, the audit will
        remain in{" "}
        <span>
          <strong>Draft</strong>
        </span>{" "}
        status.
      </p>
    </div>
  );

  return (
    <React.Fragment>
      <Tooltip title={ThirdPartyInfo} placement="bottom" arrow>
        <Icons.InfoIcon
          fontSize="small"
          sx={{
            color: AppTheme.PRIMARY_COLOR,
          }}
        />
      </Tooltip>
    </React.Fragment>
  );
}

export const ThirdPartyAuditStatusPill = ({ data }) => {
  const { pillColor, status, textColor } = getStatusChipDetailsForThirdPartyAudits(data);
  return (
    <Chip
      sx={{
        borderRadius: "3px",
        border: `1px solid ${textColor}`,
        background: pillColor,
        color: textColor,
        height: "20px",
      }}
      size="small"
      label={
        <Tooltip title={status} placement="top">
          <span style={{ fontSize: "12px" }}>
            {status}
          </span>
        </Tooltip>
      }
    />
  );
}

export const RemarkText = ({ data, size = "small", tooltipRequired = false }) => {
  const remarkDetails = getRemarkChipDetailsForThirdPartyAudits(data);
  return (
    <Tooltip title={tooltipRequired ? remarkDetails.remark : ""} placement="top">
      <span
        style={{
          fontSize: size === "small" ? "12px" : "1rem",
          fontWeight: "bold",
          color: remarkDetails.textColor,
        }}
      >
        {remarkDetails.remark}
      </span>
    </Tooltip>
  );
};

// Function to change the date settings of Audit Management Dashboard
export const handleDateSettingsForThirdPartyAudit = (FROMDATE, TODATE, FREQUENCY) => {
  const dateSettings = JSON.parse(
    localStorage.getItem(process.env.REACT_APP_DEV_THIRD_PARTY_AUDIT_DATE_SETTINGS)
  );

  const updatedDateSettings = {
    ...dateSettings,
    fromDate: FROMDATE,
    toDate: TODATE,
    frequency: FREQUENCY,
  };
  
  localStorage.setItem(
    process.env.REACT_APP_DEV_THIRD_PARTY_AUDIT_DATE_SETTINGS,
    JSON.stringify(updatedDateSettings)
  );
}